<template>
    <div class="execution-details">
        <div v-if="loading" class="text-center">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
        <div v-else class="row left-nav-collapsible">
            <div v-if="detailPageOpen" id="detail-page-open">
                <button class="btn details-open-btn" type="button" @click="onDetailPageOpenClose(false)">
                    <span> <i class="fa fa-caret-right"></i></span>
                </button>
            </div>
            <TransitionGroup :name="jobName ? 'slide' : ''" key="detail">
                <div v-if="showCloseBtn && !detailPageOpen" id="detail-page-close" key="close">
                    <button class="btn text-start details-open-btn" type="button" @click="onDetailPageOpenClose(true)">
                        <span> <i class="fa fa-caret-left"></i></span>
                    </button>
                </div>
                <div v-if="!detailPageOpen" id="details-col" @mouseover="onDetailPageHover(true)" key="details-col">
                    <div class="card">
                        <div class="card-body">
                            <div class="list-group list-group-flush my-n3">
                                <div class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Status</h5>
                                        </div>
                                        <div class="col-auto">
                                            <strong :class="[statusClass, 'text-uppercase']">{{ status }}</strong>
                                            <div v-if="status && status.toLowerCase() != 'failed' && status.toLowerCase() != 'succeeded' && status.toLowerCase() != 'cancelled' && count < 10"
                                                style="margin-left:10px"
                                                class="spinner-border spinner-border-sm text-primary">&nbsp;</div>
                                        </div>
                                    </div>
                                </div>
                                <div class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Job Name</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ jobName }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="cluster" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Cluster</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ cluster }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="gitCommit" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Commit (trunc)</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ gitCommit }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="submittedAt" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Submitted</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ submittedAt.format('MMM D, YYYY h:mm:ss A')
                                                }}</small>
                                            <span class="badge badge-secondary">{{ submittedRelativeTime }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="startedAt" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Started</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">
                                                {{ dayjs(startedAt).format('MMM D, YYYY h:mm:ssA') }}
                                            </small>
                                            <span class="badge badge-secondary">{{ startedAtRelativeTime }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="startedBy" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Started By</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ startedBy }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="completedAt" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Completed</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{
                                                dayjs(completedAt).format('MMM D, YYYY h:mm:ss A') }}</small><span
                                                class="badge badge-secondary">{{
                                                    convertToRelativeTime(dayjs(completedAt)) }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="queueTime" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Queue Time</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ convertToTime(queueTime) }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="runtime" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Runtime</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ convertToTime(runtime) }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="tag" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Tag</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ tag }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="failureReason" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Failure Reason</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ failureReason }}</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col" id="main-col" @mouseover="onDetailPageHover(false)" key="main-col">
                    <div class="row justify-content-end" v-if="enableRefreshBtn">
                        <div class="col-sm-3 d-flex position-absolute justify-content-end">
                            <button @click.prevent="onRefreshClick()" :disabled="refreshBtnLoading"
                                class="btn btn-sm btn-primary rounded-2 custom-refresh-btn">
                                <div v-if="refreshBtnLoading" class="spinner-border spinner-border-sm" role="status" />
                                Refresh
                            </button>
                        </div>
                    </div>
                    <tabs :tabs="tabItems" :btnTabs="true">
                        <template v-slot:logs>
                            <div class="row">
                                <div class="col-md-12" v-if="logOptions.length">
                                    <div class="card mb-n2 no-border">
                                        <div class="card-body">
                                            <input-field label="Select Log Type" type="select" :options="logOptions"
                                                v-model="selectedLogType" class="mb-0" />
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-12" v-if="selectedLogType == 'logs'">
                                    <div class="card no-border">

                                        <!-- <div class="card-header">
                                            <h4 class="card-header-title">
                                                Logs
                                            </h4>
                                            <div v-if="status && status.toLowerCase() != 'failed' && status.toLowerCase() != 'succeeded' && status.toLowerCase() != 'cancelled'"
                                                class="spinner-border spinner-border-sm float-end">&nbsp;</div>

                                        </div> -->
                                        <div class="card-body">
                                            <pre class="mb-0" v-if="logs" id="logs"><code>{{ logs }}</code></pre>
                                            <pre v-else><code>please wait...</code></pre>
                                            <pre class="mt-2 downloadLink "
                                                v-if="isDownloading"><code>Downloading...</code></pre>
                                            <div class="justify-content-center d-flex" v-if="enableShowMoreBtn">
                                                <button class="btn btn-sm btn-secondary mt-3 small-btn"
                                                    @click="onShowMoreClick" :disabled="showMoreBtnLoading">
                                                    <div v-if="showMoreBtnLoading"
                                                        class="spinner-border spinner-border-sm"
                                                        style="width: 1em; height: 1em;" role="status" />
                                                    Show more
                                                </button>
                                            </div>
                                            <div v-if="outputLogLink" class="p-2 downloadLink">
                                                <a :href="outputLogLink"
                                                    @click.prevent="downloadFile(outputLogLink, executionId + '_outputLog')"><span
                                                        class="fa fa-download"></span>&nbsp;Output Log</a>&nbsp; {{
                                                            outputLog }} MB
                                            </div>
                                            <div v-if="errorLogLink" class="p-2 mb-2 downloadLink">
                                                <a :href="errorLogLink"
                                                    @click.prevent="downloadFile(errorLogLink, executionId + '_errorLog')"><span
                                                        class="fa fa-download"></span>&nbsp;Error
                                                    Log&nbsp;&nbsp;&nbsp;&nbsp;</a> &nbsp;{{ errorLog }} MB
                                            </div>
                                        </div>
                                    </div>
                                    <div class="card"
                                        v-if="status && status.toLowerCase() == 'succeeded' && outputPaths.length">
                                        <div class="card-header">
                                            <h4 class="card-header-title">
                                                Output Paths
                                            </h4>
                                        </div>
                                        <div class="card-body">
                                            <ul>
                                                <li v-for="path in outputPaths" :key="path.index">{{ path }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-12" v-if="selectedLogType !== 'logs'">
                                    <div class="card no-border">
                                        <div class="card-body pt-0" id="logs">
                                            <JobExecutorLogs :sandboxName="sandboxId" :jobName="jobName"
                                                :executionId="executionId" :executorsId="selectedLogType"
                                                :jobStatus="status"></JobExecutorLogs>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-slot:logStreams>
                            <div class="card">
                                <div v-if="logStreams && logStreams.length">
                                    <div class="row justify-content-end">
                                        <div
                                            class="col-md-9 mt-4 me-4 d-flex align-items-center position-absolute justify-content-end gap-4">
                                            <div class="custom-control form-check form-switch">
                                                <input class="form-check-input" type="checkbox" role="switch"
                                                    id="AbbLogStreamSwitch" v-model="showLogDataInUnits">
                                                <label class="form-check-label" for="AbbLogStreamSwitch">Show
                                                    Abbreviated
                                                    Data</label>
                                            </div>
                                            <div v-if="status == 'running'">
                                                <button @click.prevent="getLogStreams()"
                                                    :disabled="getLogStreamsLoading" class="btn btn-sm btn-secondary">
                                                    <div v-if="getLogStreamsLoading"
                                                        class="spinner-border spinner-border-sm" role="status"
                                                        style="width: 1em; height: 1em;"></div>
                                                    <div v-else>Get Log Streams</div>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="card-body p-2">
                                        <datatable tableId="log-streams" :columns="logStreamColumns" :data="logStreams"
                                            :formatColumn="logStreamFormatColumn" :key="showLogDataInUnits"
                                            :compact="true" :showLoading="false" @viewModal="openModal"
                                            :viewModalColumn="viewModalColumn" />
                                    </div>
                                </div>
                                <div v-else class="card-body">
                                    <div class="d-flex justify-content-end" v-if="status == 'running'">
                                        <button @click.prevent="getLogStreams()" :disabled="getLogStreamsLoading"
                                            class="btn btn-sm btn-secondary">
                                            <div v-if="getLogStreamsLoading" class="spinner-border spinner-border-sm"
                                                role="status" style="width: 1em; height: 1em;"></div>
                                            <div v-else>Get Log Streams</div>
                                        </button>
                                    </div>
                                    <p>
                                    <pre>No log streams found!</pre>
                                    </p>
                                </div>
                            </div>
                        </template>
                        <template v-slot:[currentLogStreamId]>
                            <div class="card no-border">
                                <div class="card-body">
                                    <pre class="mb-0" id="logs"><code>{{ logStreamsData.Logs }}</code></pre>
                                </div>

                            </div>

                        </template>
                        <template v-slot:metrics>
                            <div class="card">
                                <div class="card-body">
                                    <div v-if="metricLoading" class="text-center mt-5">
                                        <div class="spinner-border" role="status">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                    <div v-else-if="countsV2Metrics && Object.keys(countsV2Metrics).length">
                                        <MetricsView :metricData="countsV2Metrics"
                                            :jobKind="isPromotion ? promotedJobKind : job.kind" :startedAt="startedAt"
                                            :completedAt="status == 'running' ? currentUtcTime : completedAt"
                                            :timeRange="timeRange" :aggInterval="aggInterval"
                                            @callGetCountV2Metrics="getCountV2Metrics"
                                            @onTimeRangeChange="timeRangeChange" @onIntervalChange="aggIntervalChange"
                                            :customStartTime="customStartTime" :customEndTime="customEndTime"
                                            :executionId="executionId" @apply-custom-range="handleCustomRange" />
                                    </div>
                                    <div v-else id="lake-view-metrics" class="row">
                                        <div class="col">
                                            <div class="row justify-content-end">
                                                <div v-if="metrics"
                                                    class="col-md-9 mt-4 d-flex position-absolute justify-content-end">
                                                    <div v-if="showMetricsFilterBtn"
                                                        class="custom-control form-check  me-5 form-switch">
                                                        <input class="form-check-input" type="checkbox" role="switch"
                                                            id="customSwitch1" v-model="showAllMetrics">
                                                        <label class="form-check-label" for="customSwitch1">All
                                                            Metrics</label>
                                                    </div>
                                                    <div class="custom-control form-check  me-5 form-switch">
                                                        <input class="form-check-input" type="checkbox" role="switch"
                                                            id="customSwitch2" v-model="showDataInUnits">
                                                        <label class="form-check-label" for="customSwitch2">Show
                                                            Abbreviated Data</label>
                                                    </div>
                                                </div>
                                                <div v-else class="card-body">
                                                    <p>
                                                    <pre>{{ metricsErrorMsg }}</pre>
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="card-body p-2" v-if="metrics && metrics.length">
                                                <datatable :tableId="executionId" :columns="metricColumns"
                                                    :data="filteredMetrics" :compact="true" :showLoading="false" />
                                            </div>


                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-slot:rerunHistory v-if="rerunHistory">
                            <div class="card">
                                <div class="">
                                    <datatable :tableId="`${executionId}-rerunHistory`" :columns="rerunHistoryColumns"
                                        :formatColumn="rerunHistoryFormatColumns" :orderColumn="rerunHistoryOrderColumn"
                                        :data="rerunHistory" :compact="true" :showLoading="false" :detailColumn="{
                                            renderBaseUrl: (data) => {
                                                return `${data.execution_id}`;
                                            },
                                        }" :highlightRow="(row) => row.execution_id === executionId"
                                        :highlightStyle="{ backgroundColor: 'var(--bs-light)', fontWeight: '600' }" />
                                </div>
                            </div>
                        </template>
                        <template v-slot:config v-if="Object.keys(yamlContent).length !== 0">
                            <div class="card">
                                <div class="card-body" id="execution-config-div">
                                    <job-editor v-on:tabChange="showTab" :editMode="false" v-model="yamlContent"
                                        :activeTab="activeTab"></job-editor>
                                </div>
                            </div>
                        </template>
                        <template v-slot:progress>
                            <div class="card">
                                <div class="card-body">
                                    <div class="d-flex align-items-center justify-content-between gap-2">
                                        <div class="d-flex align-items-center justify-content-start gap-2">
                                            <div v-if="pipelineView">
                                                <span class="badge bg-secondary steps-badge"
                                                    v-if="pipelineExecutionDetails?.total && pipelineExecutionDetails?.completed">
                                                    {{ pipelineExecutionDetails?.completed }}/{{
                                                        pipelineExecutionDetails?.total }} Jobs
                                                </span>
                                                <div>
                                                    <div
                                                        v-if="pipelineExecutionDetails?.status?.toLowerCase() == 'running' || pipelineExecutionDetails?.status?.toLowerCase() == 'submitted'">
                                                        <span class="spinner-border spinner-border-sm me-1"
                                                            role="status" style="width: 1em; height: 1em;">
                                                            <span class="visually-hidden">Loading...</span>
                                                        </span>
                                                        <strong
                                                            :class="[pipelineExecutionDetails?.status == 'RUNNING' ? 'text-primary' : 'text-warning']">{{
                                                                pipelineExecutionDetails?.status }}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="btn-group" role="group" style="margin-left: 8px;"
                                            aria-label="Basic radio toggle button group"
                                            v-if="job?.kind?.toLowerCase() === 'pipeline'">
                                            <button class="btn btn-sm"
                                                :class="!pipelineView ? 'btn-primary' : 'btn-default btn-light'"
                                                @click="togglePipelineView(false)">
                                                <i class="fa-solid fa-bars-progress"></i>
                                            </button>
                                            <button class="btn btn-sm"
                                                :class="pipelineView ? 'btn-primary' : 'btn-default btn-light'"
                                                @click="togglePipelineView(true)">
                                                <i class="fa-solid fa-diagram-project"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div v-if="pipelineView">
                                        <div v-if="loadingExecutionConfig" class="text-center mt-5">
                                            <div class="spinner-border spinner-border-sm me-1" role="status"
                                                style="width: 1em; height: 1em;">
                                                <span class="visually-hidden">Loading...</span>
                                            </div>
                                            Loading pipeline...
                                        </div>
                                        <div v-else>
                                            <div v-if="executionConfig" style="height: 600px; margin-top: 10px;">
                                                <pipeline-graph :pipelineJobConfig="pipelineJobConfig"
                                                    :jobs="pipelineJobsAll"
                                                    :key="`pipeline-${executionId}-${detailPageOpen}-${resetViewTrigger}`"
                                                    :pipelineExecutionDetails="pipelineExecutionDetails"
                                                    :countsMetrics="nodeMetricsInfo" @nodeClick="onNodeClick"
                                                    :showresetViewBtn="true" @resetView="resetViewTrigger++" />
                                            </div>
                                            <div v-else class="text-center mt-5">
                                                <p>No execution config found to display pipeline!</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else id="progress-config-div" style="margin-top: 10px;">
                                        <JobExecutionProgress :sandboxName="sandboxId" :jobName="jobName"
                                            :executionId="executionId" :status="status"
                                            :retryProgressTrigger="retryProgressTrigger">
                                        </JobExecutionProgress>
                                    </div>
                                </div>
                            </div>
                            <transition name="card-pop">
                                <div v-if="pipelineView && nodeInfo?.length > 0 && currentNodeInfo"
                                    class="node-info-wrapper" ref="infoCard">
                                    <div class="card shadow">
                                        <div class="card-header d-flex justify-content-between align-items-center">
                                            <span style="flex: none;">
                                                <h4 class="mb-0 fw-bold text-dark text-truncate me-2">
                                                    {{ currentNodeInfo?.name }}
                                                    <button type="button" v-if="currentNodeInfo?.name"
                                                        title="Click to view job details"
                                                        class="btn btn-sm btn-outline-primary border-0 rounded-circle"
                                                        @click.prevent="redirectToJob(currentNodeInfo)">
                                                        <i class="fa-solid fa-arrow-up-right-from-square small"></i>
                                                    </button>
                                                </h4>
                                            </span>
                                            <button type="button" class="btn-close"
                                                @click.prevent="nodeInfo = null; currentNodeInfo = null"></button>
                                        </div>
                                        <div class="card-body">
                                            <div class="row">
                                                <div v-if="nodeInfo.length > 1 && isLoopNode(currentNodeInfo)"
                                                    class="col-3" style="max-height: 350px; overflow-y: auto;">
                                                    <h5 class="mb-3">
                                                        Loop Iterations
                                                        <i class="fa fa-circle-info text-muted"
                                                            style="margin-left: 1px;"
                                                            :data-bs-title="`List of all the iterations in <strong>${currentNodeInfo?.name}</strong>`"
                                                            data-bs-toggle="tooltip" data-bs-placement="right"
                                                            data-bs-html="true"></i>
                                                    </h5>
                                                    <div class="list-group list-group-flush"
                                                        style="border-left: 2.5px solid var(--bs-primary);">
                                                        <div v-for="(task, index) in nodeInfo" :key="task.task_id"
                                                            @click="currentNodeInfo = task"
                                                            :class="['list-group-item', 'task-item', task.task_id === currentNodeInfo?.task_id ? 'active-task-item text-primary' : '']">
                                                            <div class="row align-items-center">
                                                                <div class="col">
                                                                    <h5 class="mb-0">
                                                                        {{ `#${index + 1} - ${task.task_id}` }}
                                                                        <i v-if="task.task_id === currentNodeInfo?.task_id"
                                                                            class="fa-solid fa-eye ms-1"></i>
                                                                    </h5>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div
                                                    :class="[nodeInfo.length > 1 && isLoopNode(currentNodeInfo) ? 'col-9 border-start' : 'col']">
                                                    <div class="list-group list-group-flush">
                                                        <div v-if="currentNodeInfo?.task_id" class="list-group-item">
                                                            <div class="row align-items-center">
                                                                <div class="col">
                                                                    <h5 class="mb-0">
                                                                        Task ID
                                                                        <span class="fa fa-circle-info text-muted"
                                                                            style="margin-left: 1px;"
                                                                            :data-bs-title="'Task ID'"
                                                                            data-bs-toggle="tooltip"
                                                                            data-bs-placement="right"></span>
                                                                    </h5>
                                                                </div>
                                                                <div class="col-auto">
                                                                    <small class="text-muted fw-bold">{{
                                                                        currentNodeInfo?.task_id
                                                                        }}</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div v-if="currentNodeInfo?.status" class="list-group-item">
                                                            <div class="row align-items-center">
                                                                <div class="col">
                                                                    <h5 class="mb-0">
                                                                        Status
                                                                        <span class="fa fa-circle-info text-muted"
                                                                            style="margin-left: 1px;"
                                                                            :data-bs-title="'Task Status'"
                                                                            data-bs-toggle="tooltip"
                                                                            data-bs-placement="right"></span>
                                                                    </h5>
                                                                </div>
                                                                <div class="col-auto">
                                                                    <span
                                                                        v-if="currentNodeInfo?.status.toLowerCase() == 'running' || currentNodeInfo?.status.toLowerCase() == 'submitted'"
                                                                        :class="['me-1', statusClassMap[currentNodeInfo?.status.toLowerCase()]]">●</span>
                                                                    <small class="fw-bold"
                                                                        :class="statusClassMap[currentNodeInfo?.status.toLowerCase()]">{{
                                                                            currentNodeInfo?.status }}</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div v-if="currentNodeInfo?.full_path" class="list-group-item">
                                                            <div class="row align-items-center">
                                                                <div class="col" style="width: 100px;">
                                                                    <h5 class="mb-0">
                                                                        Full Path
                                                                        <span class="fa fa-circle-info text-muted"
                                                                            style="margin-left: 1px;"
                                                                            data-bs-toggle="tooltip"
                                                                            data-bs-placement="right"
                                                                            :data-bs-title="'Task full path is a string that uniquely identifies the hierarchical position and sequence of a specific task within the overall pipeline execution'"></span>
                                                                    </h5>
                                                                </div>
                                                                <div class="col-auto">
                                                                    <small class="text-muted">{{
                                                                        currentNodeInfo?.full_path
                                                                        }}</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div v-if="currentNodeInfo?.sandbox_name"
                                                            class="list-group-item">
                                                            <div class="row align-items-center">
                                                                <div class="col">
                                                                    <h5 class="mb-0">
                                                                        Sandbox Name
                                                                        <span class="fa fa-circle-info text-muted"
                                                                            style="margin-left: 1px;"
                                                                            data-bs-toggle="tooltip"
                                                                            data-bs-placement="right"
                                                                            :data-bs-title="'The name of the sandbox where the task\'s job resides'"></span>
                                                                    </h5>
                                                                </div>
                                                                <div class="col-auto">
                                                                    <small class="text-muted fw-bold">{{
                                                                        currentNodeInfo?.sandbox_name
                                                                        }}</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div v-if="currentNodeInfo?.created_at" class="list-group-item">
                                                            <div class="row align-items-center">
                                                                <div class="col">
                                                                    <h5 class="mb-0">
                                                                        Created At
                                                                        <span class="fa fa-circle-info text-muted"
                                                                            style="margin-left: 1px;"
                                                                            :data-bs-title="'Timestamp when the task was created'"
                                                                            data-bs-toggle="tooltip"
                                                                            data-bs-placement="right"></span>
                                                                    </h5>
                                                                </div>
                                                                <div class="col-auto">
                                                                    <small class="text-muted">
                                                                        {{ dayjs(currentNodeInfo?.created_at)
                                                                            .format('MMM D, YYYYh:mm:ss A') }}
                                                                    </small>
                                                                    <span class="badge badge-secondary ms-2">
                                                                        {{
                                                                            convertToRelativeTime(dayjs(currentNodeInfo?.created_at))
                                                                        }}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div v-if="currentNodeInfo?.updated_at" class="list-group-item">
                                                            <div class="row align-items-center">
                                                                <div class="col">
                                                                    <h5 class="mb-0">
                                                                        Updated At
                                                                        <span class="fa fa-circle-info text-muted"
                                                                            style="margin-left: 1px;"
                                                                            :data-bs-title="'The timestamp when the task last was updated'"
                                                                            data-bs-toggle="tooltip"
                                                                            data-bs-placement="right"></span>
                                                                    </h5>
                                                                </div>
                                                                <div class="col-auto">
                                                                    <small class="text-muted">
                                                                        {{ dayjs(currentNodeInfo?.updated_at)
                                                                            .format('MMM D, YYYYh: mm: ss A') }}
                                                                    </small>
                                                                    <span class="badge badge-secondary ms-2">
                                                                        {{
                                                                            convertToRelativeTime(dayjs(currentNodeInfo?.updated_at))
                                                                        }}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <h5 style="margin-top: 20px;">
                                                                Executions
                                                                <span class="fa fa-circle-info text-muted mb-3"
                                                                    style="margin-left: 1px;"
                                                                    :data-bs-title="'List of executions for this task'"
                                                                    data-bs-toggle="tooltip" data-bs-placement="right">
                                                                </span>
                                                            </h5>
                                                            <div v-if="currentNodeInfo?.executions?.length > 0">
                                                                <datatable
                                                                    :tableId="`${currentNodeInfo?.task_id}-executions`"
                                                                    :columns="executionsColumns" :searching="false"
                                                                    :formatColumn="executionsFormatColumns"
                                                                    :data="modifiedExecutionsData" :compact="true"
                                                                    :showLoading="false" :ignoreCache="true"
                                                                    :detailColumn="{
                                                                        renderBaseUrl: (data) => {
                                                                            const cid = this.$store.state.currentClient?.cid;
                                                                            const taskJobName = currentNodeInfo?.name;
                                                                            const sandboxName = currentNodeInfo?.sandbox_name || sandboxId;
                                                                            const base = this.isDevSandbox
                                                                                ? `/${cid}/amdp/sandbox/${sandboxName}/jobs/${taskJobName}/execution-details/`
                                                                                : `/${cid}/amdp/${sandboxName}/jobs/${taskJobName}/execution-details/`;
                                                                            return base + data.execution_id;
                                                                        },
                                                                    }" />
                                                            </div>
                                                            <div v-else>
                                                                <div
                                                                    class="text-center py-3 bg-light rounded border border-dashed">
                                                                    <span class="text-muted">
                                                                        <i class="fa fa-info-circle"></i>
                                                                        No executions found for this task
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div v-if="currentNodeInfo?.status?.toLowerCase() === 'failed' || currentNodeInfo?.status?.toLowerCase() === 'cancelled'"
                                                        class="d-flex justify-content-between align-items-center mt-3">
                                                        <div>
                                                            <span v-if="markSuccessError" class="text-danger">
                                                                <i class="fa-solid fa-circle-exclamation"></i>
                                                                {{ markSuccessError }}
                                                            </span>
                                                        </div>
                                                        <button class="btn btn-sm btn-primary"
                                                            @click="markTaskAsSuccess(currentNodeInfo?.task_id)"
                                                            :disabled="markingTaskSuccess || currentNodeInfo?.marked_success">
                                                            <span v-if="markingTaskSuccess">
                                                                <span class="spinner-border spinner-border-sm me-1"
                                                                    role="status" style="width: 1em; height: 1em;">
                                                                    <span class="visually-hidden">Loading...</span>
                                                                </span>
                                                                <span>
                                                                    Marking
                                                                </span>
                                                            </span>
                                                            <span v-else>
                                                                <i v-if="currentNodeInfo?.marked_success"
                                                                    class="fa fa-check"></i>
                                                                {{ currentNodeInfo?.marked_success ? 'Marked' : 'Mark'
                                                                }} as
                                                                Successful
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </transition>
                        </template>
                    </tabs>
                </div>
            </TransitionGroup>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc'
import duration from 'dayjs/plugin/duration'
import Datatable from "../../../components/Datatable.vue";
import { secondsToHourMinSec, formatBytes, relativeTime } from '../../../utils/commonFunction';
import Tabs from "../../../components/Tabs.vue";
import JobEditor from '../../../components/sandbox/JobEditor.vue';
import JobExecutionProgress from '../../../components/sandbox/JobExecutionProgress.vue';
import JobExecutorLogs from '../../../components/sandbox/JobExecutorLogs.vue';
import MetricsView from '../../../components/sandbox/Metrics.vue';
import { mapState, mapActions } from "vuex";
import InputField from "../../../components/InputField.vue";
import PipelineGraph from '../../agm/pipelineGraph.vue';
import YAML from 'yaml';
dayjs.extend(duration)
dayjs.extend(utc)

export default {
    components: { Datatable, Tabs, JobEditor, JobExecutionProgress, JobExecutorLogs, InputField, MetricsView, PipelineGraph },
    data() {
        return {
            statusClassMap: {
                "succeeded": "text-success",
                "failed": "text-danger",
                "running": "text-primary",
                "submitted": "text-warning",
                "cancelled": "text-secondary",
            },
            tabItems: [
                {
                    name: "Progress",
                    id: "id1",
                    slot: "progress"
                },
                {
                    name: "Metrics",
                    id: "id3",
                    slot: "metrics"
                },
                {
                    name: "Logs",
                    id: "id2",
                    slot: "logs"
                },
                {
                    name: "Log Streams",
                    id: "id5",
                    slot: "logStreams"

                },
                {
                    name: "Config",
                    id: "id4",
                    slot: "config",
                    hide: true
                },
                {
                    name: "Rerun History",
                    id: "id6",
                    slot: "rerunHistory",
                    hide: true
                }
            ],
            jobObj: this.job,
            name: null,
            status: null,
            statusClass: 'text-primary',
            cluster: null,
            gitCommit: null,
            submittedAt: null,
            submittedRelativeTime: null,
            startedAt: null,
            startedAtRelativeTime: null,
            startedBy: null,
            completedAt: null,
            completedAtRelativeTime: null,
            queueTime: null,
            runtime: null,
            failureReason: null,
            duration: 0,
            tag: null,
            executionId: this.$route.params.executionId,
            jobName: this.$route.params.jobId,
            sandboxId: this.$route.params.sandboxId,
            logs: "",
            logsError: false,
            breakCheck: false,
            outputPaths: [],
            metrics: null,
            defaultMetrics: null,
            filteredMetrics: null,
            metricColumns: [],
            showAllMetrics: false,
            showDataInUnits: true,
            showMetricsFilterBtn: true,
            yamlContent: {},
            executionConfig: null,
            loadingExecutionConfig: false,
            activeTab: "",
            outputLogLink: null,
            errorLogLink: null,
            outputLog: 0,
            errorLog: 0,
            isDownloading: null,
            metricsErrorMsg: null,
            loading: true,
            executorsArray: [],
            selectedLogType: "logs",
            detailPageOpen: false,
            showCloseBtn: false,
            logStreams: [],
            logStreamColumns: [],
            logStreamFormatColumn: [
                {
                    index: 4,
                    type: "datetime",
                },
            ],
            showLogDataInUnits: true,
            currentLogStream: null,
            currentLogStreamId: null,
            logStreamsData: [],
            viewModalColumn: 1,
            countsV2Metrics: {},
            metricLoading: true,
            timeRange: (this.job && this.job.kind == 'MAPPER-ΜBATCH') ? "overall" : null,
            aggInterval: null,
            customStartTime: null,
            customEndTime: null,
            currentUtcTime: dayjs.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
            count: 1,
            executionStatus: false,
            enableRefreshBtn: false,
            enableShowMoreBtn: false,
            refreshBtnLoading: false,
            showMoreBtnLoading: false,
            getLogStreamsLoading: false,
            retryProgressTrigger: false,
            promotedJobKind: null,

            // rerun history info
            rerunHistory: null,
            rerunHistoryColumns: [
                {
                    data: "execution_id",
                    title: "Execution ID",
                    render: function (data, type, row) {
                        if (row.src_job_execution) {
                            return `${data} <span class="badge" style="background-color: tomato;">Source Execution</span>`;
                        }
                        return data;
                    }
                },
                {
                    data: "status"
                },
                {
                    data: "submitted_at",
                    title: "submitted At"
                },
            ],
            rerunHistoryFormatColumns: [
                {
                    index: 3,
                    type: "datetime"
                }
            ],
            rerunHistoryOrderColumn: [3, "desc"],

            // pipeline view
            pipelineJobsAll: {},
            pipelineView: this.job?.kind?.toLowerCase() === 'pipeline' || false,
            pipelineExecutionDetails: {},
            pipelineBreakCheck: false,
            pipelineTracking: false, // whether the trackPipelineExecutionStatus method is running
            pipelineRunning: false, // whether the pipeline is running
            nodeInfo: null, // node info to display below pipeline
            currentNodeInfo: null, // current node info to display below pipeline
            nodeMetricsInfo: {}, // node metrics to display when hovering the node
            executionsColumns: [
                {
                    data: "execution_id",
                    title: "ID",
                    render: function (data, type, row) {
                        // if tag is rerun, add a layer group icon after the id
                        if (row.tag === "rerun") {
                            return `<span> ${data} <i class="fa fa-layer-group ms-1 text-primary"></i></span>`;
                        }
                        return data;
                    }
                },
                {
                    data: "status"
                },
                {
                    data: "queue_time",
                    title: "Queue Time",
                    render: function (data, type, row) {
                        return secondsToHourMinSec(data);
                    }
                },
                {
                    data: "run_time",
                    title: "Run Time",
                    render: function (data, type, row) {
                        return secondsToHourMinSec(data);
                    }
                },
                {
                    data: "submitted_at",
                    title: "Submitted At",
                },
                {
                    data: "completed_at",
                    title: "Completed At",
                },
            ],
            executionsFormatColumns: [
                {
                    index: 5,
                    type: "datetime"
                },
                {
                    index: 6,
                    type: "datetime"
                }
            ],

            // marking task as success
            markingTaskSuccess: false,
            markSuccessError: null,

            // pipeline view reset view trigger
            resetViewTrigger: 0,
        }
    },
    props: [
        "cancelJobTrigger",
        "isSandbox",
        "saveJobConfigTrigger",
        "job",
        "isPromotion",
        "promotion",
        "reRunJobTrigger",
        "reRunJobTriggerWithOverride",
        "reRunJobTriggerWithImage",
        "pipelineJobs",
        "override"
    ],
    computed: {
        baseEndpoint() {
            let endpoint = `${this.$store.state.lakeviewUrl}/v1/cids/${this.$store.state.currentClient.cid}/sandboxes/`;
            return endpoint + this.$store.state.sandbox.currentSandbox
        },

        ...mapState({
            jobKinds: (state) => state.amdp.jobKinds
        }),
        logOptions() {
            let options = [];
            if (this.executorsArray.length) {
                options.push({ "label": "Driver Logs", "value": "logs" });
                for (const log of this.executorsArray) {
                    options.push({ "label": log.id, "value": log.id });
                }
            }
            return options;
        },
        isDevSandbox() {
            let devSandboxes = this.$store.state.sandbox.devSandboxes;
            devSandboxes = devSandboxes.filter((sandbox => sandbox.name === this.sandboxId))
            return devSandboxes.length ? true : false;
        },
        pipelineJobConfig() {
            return { ...this.job, ...this.executionConfig };
        },
        modifiedExecutionsData() {
            // add status flag to the executions data.status column
            return this.currentNodeInfo?.executions?.map(execution => {
                let status = "";
                if (execution.status.toLowerCase() === "succeeded") {
                    status = "<span class='text-success'>●</span> ";
                } else if (execution.status.toLowerCase() === "failed") {
                    status = "<span class='text-danger'>●</span> ";
                } else if (execution.status.toLowerCase() === "running") {
                    status = "<span class='text-primary'>●</span> ";
                } else if (execution.status.toLowerCase() === "submitted") {
                    status = "<span class='text-warning'>●</span> ";
                } else if (execution.status.toLowerCase() === "cancelled") {
                    status = "<span class='text-secondary'>●</span> ";
                }
                execution["status"] = status + execution.status;
                return execution;
            });
        }
    },
    watch: {
        cancelJobTrigger() {
            this.cancelJob();
        },
        showAllMetrics(newVal) {
            if (newVal)
                this.filteredMetrics = this.metrics;
            else
                this.filterInputAndOutMetrics();
        },
        showDataInUnits(newVal) {
            this.convertMetricDataToUnits();
        },
        saveJobConfigTrigger() {
            this.saveJob()
        },
        job(newVal) {
            this.jobObj = newVal;
            if (newVal?.kind?.toLowerCase() === 'pipeline') {
                this.trackPipelineExecutionStatus(this.executionId, true);
                this.pipelineView = true;
            } else {
                this.pipelineView = false;
            }
        },
        async reRunJobTrigger() {
            this.$emit('reRunning', true);
            await this.rerunJob();
            this.$emit('reRunning', false);
        },
        async reRunJobTriggerWithOverride() {
            this.$emit('reRunning', true);
            await this.rerunJob(true);
            this.$emit('reRunning', false);
        },
        reRunJobTriggerWithImage() {
            this.showModal({
                component: "JobReRun",
                data: {
                    jobName: this.jobName,
                    sandboxName: this.sandboxId,
                    executionId: this.executionId,
                    routeNameAfterReRun: this.$store.state.sandbox.devSandboxes.map(sandbox => { return sandbox.name })
                        .includes(this.$store.state.sandbox.currentSandbox) ? "Sandbox Job Execution Details" : "Job Execution Details",
                    overrideCapacity: !!this.override,
                }
            })
        },
        nodeInfo(newValue, oldValue) {
            if (newValue && !oldValue) {
                // Use nextTick to wait for the DOM element to be created by v-if
                this.$nextTick(() => {
                    if (this.$refs.infoCard) {
                        // scrollIntoView is the cleanest modern way to do this
                        this.$refs.infoCard.scrollIntoView({
                            behavior: 'smooth',
                            block: 'end' // Scrolls until the bottom of the card is visible
                        });
                    }
                });
            }
        },
        pipelineJobs(newValue, oldValue) {
            if (newValue) {
                this.fetchExtraJobsInExecution();
            }
        }
    },
    methods: {
        ...mapActions({
            showModal: "showModal",
            cancelSandboxJobExecution: "sandbox/cancelSandboxJobExecution",
            updateSandboxJob: "sandbox/updateSandboxJob",
            readJob: "amdp/readJob",
            reRunSandboxJob: "sandbox/reRunSandboxJob",
            readSandboxJob: "sandbox/readSandboxJob",
        }),
        dayjs,
        async rerunJob(overrideCapacity = false) {
            await this.reRunSandboxJob({
                sandboxName: this.sandboxId,
                jobName: this.jobName,
                executionId: this.executionId,
                overrideCapacity: overrideCapacity,
                payload: {},
            }).then((response) => {
                // reroute to the corresponding job execution details page
                const executionId = response.data.execution_id;
                this.$router.push({
                    name: this.$store.state.sandbox.devSandboxes.map(sandbox => { return sandbox.name })
                        .includes(this.$store.state.sandbox.currentSandbox) ? "Sandbox Job Execution Details" : "Job Execution Details",
                    params: {
                        cid: this.$store.state.currentClient.cid,
                        sandboxId: this.sandboxId,
                        jobId: this.jobName,
                        executionId: executionId,
                    },
                });
            }).catch((error) => {
                console.error("Error rerunning job: ", error);
                this.$store.dispatch('addToast', {
                    message: error.response?.data?.error || error.message || "Something went wrong while rerunning the job. Please contact support.",
                    type: 'error'
                });
            });
        },
        async cancelJob() {
            try {
                this.$emit('setRunClick', true);
                const response = await this.cancelSandboxJobExecution({ jobName: this.jobName, executionId: this.executionId });
                this.response = 'Job cancelled successfully.'
            } catch (error) {
                if (error.response && error.response.data && error.response.data.error)
                    self.error = error.response.data.error;
                else self.error = error.message;

                const options = {
                    message: self.error || "Error while cancelling Job",
                    type: "error",
                };
                this.$store.dispatch("addToast", options);
            } finally {
                this.loading = false;
                this.$emit('setRunClick', false);
                this.$emit('showCancelJob', false);
            }

        },
        async handleJobChecking() {
            if (this.breakCheck)
                return;

            const logsPromise = this.getExecutionLogs()
            const executorsPromise = this.getExecutors()
            const statusPromise = await this.getExecutionStatus()

            if (this.count < 10 && (this.logsError || (this.status && this.status != 'failed' && this.status != 'succeeded' && this.status != 'cancelled'))) {
                this.count++;
                await new Promise(resolve => setTimeout(resolve, 2000)); // sleep for 2s
                await this.handleJobChecking();
            } else {
                this.$emit('showCancelJob', false);
                if (this.isPromotion) {
                    this.$emit('getPromotionRunList', true);
                }
            }
        },
        getExecutionStatus() {
            let self = this;
            return axios.get(this.baseEndpoint + `/jobs/${this.jobName}/executions/${this.executionId}`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
            }).then((response) => {
                self.loading = false;
                if (response.data) {
                    this.executionStatus = true
                    const runData = response.data;
                    this.status = runData.status.toLowerCase();
                    if (this.status == 'succeeded') {
                        this.statusClass = 'text-success'
                    } else if (this.status == 'failed') {
                        this.statusClass = 'text-danger'
                        this.failureReason = runData.failure_reason
                    } else if (this.status == 'cancelled') {
                        this.statusClass = 'text-danger';
                        this.logs = "No Log files";
                    } else {
                        this.$emit('showCancelJob', true);
                    }


                    this.name = runData.name;
                    this.cluster = runData.cluster;

                    this.gitCommit = runData.git_commit ? runData.git_commit.slice(0, 8) : null;
                    this.tag = runData.tag;

                    this.startedBy = runData.created_by
                    if (runData.completed_at) {
                        this.completedAt = runData.completed_at;
                    }

                    if (runData.submitted_at) {
                        this.submittedAt = dayjs(runData.submitted_at);
                        this.submittedRelativeTime = this.convertToRelativeTime(this.submittedAt);
                    }

                    if (runData.started_at) {
                        this.startedAt = runData.started_at;
                        this.startedAtRelativeTime = this.convertToRelativeTime(dayjs(this.startedAt));
                    }
                    if (runData.queue_time) {
                        this.queueTime = runData.queue_time
                    }
                    if (runData.run_time) {
                        this.runtime = runData.run_time
                    }

                    if (this.status == 'succeeded') {
                        this.$store.dispatch("sandbox/fetchSandboxJobRuns", { jobName: this.job.name });
                        this.$emit('hidePromoteBtn', false);
                    }

                    // // Turnoff progress tag based on job status
                    // this.tabItems = this.tabItems.map(function (el) {
                    //     if ((el.slot == "progress") && self.status == "running" && el.hide) {
                    //         self.$store.dispatch('setActiveTab', "Progress");
                    //         el.hide = false;
                    //     } else if ((el.slot == "progress") && self.status != "running" && !el.hide) {
                    //         el.hide = true;
                    //         self.$store.dispatch('setActiveTab', "Metrics")
                    //     }
                    //     return el
                    // });

                    if ((this.status == 'failed' || this.status == 'cancelled') && this.tag != "rerun") {
                        this.$emit('hideReRunJobBtn', false);
                    }

                    if (runData.execution_history) {
                        this.rerunHistory = runData.execution_history.map((execution, index) => {
                            let executionObj = {
                                execution_id: execution.execution_id,
                                status: execution.status,
                                submitted_at: execution.submitted_at,
                                // last execution is assumed to be the source execution
                                src_job_execution: runData.execution_history.length - 1 === index,
                            }

                            let status = "";
                            if (executionObj.status.toLowerCase() === "succeeded") {
                                status = "<span class='text-success'>●</span> ";
                            } else if (executionObj.status.toLowerCase() === "failed") {
                                status = "<span class='text-danger'>●</span> ";
                            } else if (executionObj.status.toLowerCase() === "running") {
                                status = "<span class='text-primary'>●</span> ";
                            } else if (executionObj.status.toLowerCase() === "submitted") {
                                status = "<span class='text-warning'>●</span> ";
                            } else if (executionObj.status.toLowerCase() === "cancelled") {
                                status = "<span class='text-secondary'>●</span> ";
                            }
                            executionObj.status = status + executionObj.status;
                            return executionObj;
                        });

                        // enable rerun history tab
                        this.tabItems = this.tabItems.map(function (el) {
                            if (el.slot == "rerunHistory") {
                                el.hide = false;
                            }
                            return el;
                        });
                    }
                }
            }).catch((error) => {
                if (error.response && error.response.data && error.response.data.error)
                    this.error = error.response.data.error;
                else this.error = error.message;

                if (!this.status) {
                    let toastoptions = {
                        message: "Something went wrong while fetching execution details. Please contact support.",
                        type: "error"
                    }
                    this.$store.dispatch('addToast', toastoptions);
                }
                this.$emit('hideReRunJobBtn', true);
            });
        },
        checkAndGetCountsMetrics() {
            if (this.executionStatus) {
                this.getCountV2Metrics();
                this.executionStatus = false
            }
        },
        getExecutors() {
            return axios.get(this.baseEndpoint + `/jobs/${this.jobName}/executions/${this.executionId}/logs/executors`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`
                }
            }).then((response) => {
                if (response.data)
                    this.executorsArray = response.data.executors || [];
                else
                    this.executorsArray = [];
            }).catch((error) => {
                this.executorsArray = [];
            })

        },
        getLogStreams() {
            this.getLogStreamsLoading = true;
            return axios.get(this.baseEndpoint + `/jobs/${this.jobName}/executions/${this.executionId}/logs/log-streams`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`
                }
            }).then((response) => {
                if (response.data) {
                    this.logStreams = response.data.log_streams || [];
                }
                else {
                    this.logStreams = [];
                }

            }).catch((error) => {
                this.logStreams = [];
            }).finally(() => {
                this.getLogStreamsLoading = false;
            })

        },
        getLogStreamByID(stream) {
            return axios.get(this.baseEndpoint + `/jobs/${this.jobName}/executions/${this.executionId}/logs?source=${stream.source}&log_stream_id=${stream.id}&type=${stream.type}`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`
                }
            }).then((response) => {
                if (response.data)
                    this.logStreamsData = response.data || [];
                else
                    this.logStreamsData = [];
            }).catch((error) => {
                this.logStreamsData = [];
            })

        },
        getExecutionLogs() {
            return axios.get(this.baseEndpoint + `/jobs/${this.jobName}/executions/${this.executionId}/logs`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`
                }
            }).catch((error) => {
                this.logsError = true
                if (error.response.data.error)
                    //don't show pre-job errors
                    if (error.response.data.error.indexOf("the server rejected our request") != -1)
                        this.logs = "";
                    else
                        this.logs = error.response.data.error
                else
                    this.logs = error.message
            }).then((response) => {
                this.logsError = false;
                if (response && response.data) {
                    if (this.logs != response.data.Logs) {
                        this.logs = response.data.Logs

                        const regex = /Copied to S3: WrappedArray\((.*)\)/gi
                        const matches = [...this.logs.matchAll(regex)]
                        let locations = []
                        for (const match of matches) {
                            if (match[1]) {
                                let paths = match[1].split(',').map((path) => {
                                    return 's3://' + path;
                                })
                                locations = locations.concat(paths)
                            }
                        }
                        this.outputPaths = locations

                        //scroll to bottom of logs
                        setTimeout(() => {
                            var logsDiv = document.getElementById("logs");
                            if (logsDiv)
                                logsDiv.scrollTop = logsDiv.scrollHeight;
                        }, 0)
                    }
                    if (response.data.log_files) {
                        let logfiles = response.data.log_files
                        this.outputLogLink = logfiles["stdout.txt"].url;
                        this.outputLog = (logfiles["stdout.txt"].length / (1024 ** 2)).toFixed(2);
                        this.errorLogLink = logfiles["stderr.txt"].url;
                        this.errorLog = (logfiles["stderr.txt"].length / (1024 ** 2)).toFixed(2);
                    }
                } else {
                    this.logs = "No Log files";
                }

            }).finally(() => {
                this.loadMore = false;
            });
        },
        getExecutionConfig() {
            this.loadingExecutionConfig = true;
            return axios.get(this.baseEndpoint + `/jobs/${this.jobName}/executions/${this.executionId}/configs`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
            }).then((response) => {
                if (response.data) {
                    // save the execution config to the executionConfig object
                    this.executionConfig = response.data;

                    let yamlContent = {};
                    const promotionExceutionFlag = this.$route.params.promotionId ? true : false;
                    if (response.data.system_files) {
                        for (const i of response.data.system_files) {
                            if (promotionExceutionFlag && i.relative_path == "vars.yaml") {
                                yamlContent[i.relative_path] = i.content
                            } else if (!promotionExceutionFlag) {
                                yamlContent[i.relative_path] = i.content
                            }

                        }
                    }
                    if (response.data.user_files) {
                        for (const i of response.data.user_files) {
                            if (promotionExceutionFlag && i.relative_path == "vars.yaml") {
                                yamlContent[i.relative_path] = i.content
                            } else if (!promotionExceutionFlag) {
                                yamlContent[i.relative_path] = i.content
                            }
                        }
                    }
                    if (Object.keys(yamlContent).length) {
                        this.tabItems = this.tabItems.map(function (el) {
                            if (el.slot == "config")
                                el.hide = false;

                            return el
                        });
                        this.activeTab = Object.keys(yamlContent)[0];
                        this.yamlContent = JSON.parse(JSON.stringify(yamlContent));

                    }

                }

            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                this.loadingExecutionConfig = false;
            });
        },
        timeRangeChange(timeRange) {
            this.timeRange = timeRange;
        },
        aggIntervalChange(interval) {
            this.aggInterval = interval;
        },
        getCountV2Metrics() {
            let countsPayload = {};

            if (this.timeRange) {
                countsPayload.aggregate = true;
                if (this.timeRange == "last_hour") {
                    const endTime = (this.status === 'running' ? dayjs.utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : this.completedAt)
                    countsPayload.start_time = dayjs
                        .utc(endTime)
                        .startOf("hour")
                        .format("YYYY-MM-DD HH:mm:ss");
                    countsPayload.end_time = dayjs
                        .utc(endTime)
                        .add(1, "hour")
                        .startOf("hour")
                        .format("YYYY-MM-DD HH:mm:ss");

                } else if (this.timeRange == "custom_range") {
                    countsPayload.start_time = dayjs
                        .utc(this.customStartTime)
                        .format("YYYY-MM-DD HH:mm:ss");
                    countsPayload.end_time = dayjs
                        .utc(this.customEndTime)
                        .format("YYYY-MM-DD HH:mm:ss");
                    if (this.aggInterval)
                        countsPayload.time_interval = this.aggInterval;

                }


            }
            countsPayload.filter_dimensions = {
                "cid": this.$store.state.currentClient.cid,
                "sandbox_name": this.sandboxId,
                "job_name": this.jobName,
                "job_run_id": this.executionId
            },
                countsPayload.metrics = this.$store.state.amdp.countsMetricReqObj;


            return axios.post(`${this.$store.state.countsUrl}/v2/cids/${this.$store.state.currentClient.cid}/metrics/batch/query`, countsPayload, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`
                }
            }).then((response) => {
                if (response.data)
                    this.countsV2Metrics = response.data.data || {};
                else
                    this.countsV2Metrics = {};
            }).catch((error) => {
                this.countsV2Metrics = {};
            }).finally(() => {
                this.metricLoading = false;
                if (!Object.keys(this.countsV2Metrics).length) {
                    this.convertMetricDataToUnits();
                }

            })
        },
        filterInputAndOutMetrics() {
            if (this.metrics) {
                this.filteredMetrics = this.metrics.filter((metric) => {
                    return (metric.type == "input" || metric.type == "output")
                });

                if (this.filteredMetrics.length == this.metrics.length)
                    this.showMetricsFilterBtn = false;
                else
                    this.showMetricsFilterBtn = true;
            }
        },
        convertMetricDataToUnits() {
            let self = this;
            this.metricLoading = false;
            this.metrics.map((metric) => {
                for (const key in metric) {
                    const filterMetricColumns = self.metricColumns.filter((column) => { return key == column.data })
                    if (!filterMetricColumns.length) {
                        let tempKey = key
                        if (key == "byte_count") {
                            self.metricColumns.push({
                                data: key,
                                title: tempKey.replace(/-/g, ''),
                                render: function (data, type, row) {
                                    if (!data) return "";
                                    if (!self.showDataInUnits) return data;
                                    return formatBytes(data)
                                },
                            })
                        } else if (key == "record_count") {
                            self.metricColumns.push({
                                data: key,
                                title: tempKey.replace(/-/g, ''),
                                render: function (data, type, row) {
                                    if (!data) return "";
                                    if (!self.showDataInUnits) return data;
                                    return Intl.NumberFormat("en", { notation: "compact", roundingMode: "floor", maximumFractionDigits: 2 }).format(data)
                                },
                            })
                        } else {
                            self.metricColumns.push({
                                data: key,
                                title: tempKey.replace(/-/g, '')
                            })

                        }

                    }
                }
            });

            this.filterInputAndOutMetrics();
        },

        showTab(tab) {
            this.activeTab = tab;
        },
        convertToTime(seconds) {
            return secondsToHourMinSec(seconds);
        },
        convertToRelativeTime(timestamp) {
            return relativeTime(Date.now(), timestamp);
        },
        async downloadFile(url, label) {
            this.isDownloading = true;
            await axios.get(url,
                {
                    responseType: 'blob'
                }).then(response => {

                    var blob = new Blob([response.data], { type: "text/plain" });
                    var url = window.URL.createObjectURL(blob);
                    var a = document.createElement("a");
                    a.href = url;
                    a.download = label;
                    a.click();
                });
            this.isDownloading = null
        },
        async saveJob() {
            this.loading = true;
            let toastoptions;
            try {
                let systemFileKeys = [];
                let userFileKeys = [];
                for (const i of Object.keys(this.yamlContent)) {
                    let isSystemFile = false;
                    if (this.job.system_files) {
                        for (const j of this.job.system_files) {
                            if (j.relative_path == i)
                                isSystemFile = true;
                        }
                    }
                    if (isSystemFile)
                        systemFileKeys.push(i)
                    else
                        userFileKeys.push(i)
                }

                this.jobObj.system_files = systemFileKeys.map((key) => {
                    return { content: (typeof this.yamlContent[key] === 'string') ? this.yamlContent[key] : JSON.stringify(this.yamlContent[key]), relative_path: key };
                });

                this.jobObj.user_files = userFileKeys.map((key) => {
                    return { content: (typeof this.yamlContent[key] === 'string') ? this.yamlContent[key] : JSON.stringify(this.yamlContent[key]), relative_path: key };
                });
                const configPayload = {
                    name: this.jobObj.name,
                    system_files: this.jobObj.system_files,
                    user_files: this.jobObj.user_files,
                };
                const response = await this.updateSandboxJob(configPayload);
                this.$emit('setSnapshot', this.jobObj);
                toastoptions = {
                    message: "Job updated successfully.",
                    type: "success"
                }
                this.$router.push({
                    name: "Sandbox Job Configuration",
                    params: {
                        sandboxId: `${this.$store.state.sandbox.currentSandbox}`,
                        jobId: `${this.jobObj.name}`
                    },
                });
            } catch (error) {
                if (error.response && error.response.data && error.response.data.error)
                    this.error = error.response.data.error;
                else this.error = error.message;

                toastoptions = {
                    message: this.error || "Something went wrong when updating Job. Please contact support.",
                    type: "error"
                }
            } finally {
                this.loading = false;
                this.$store.dispatch('addToast', toastoptions);
            }
        },
        showRowDetail(index) {
            let streamIndex = this.tabItems.findIndex((tabs) => tabs.slot == "logStreams");

            this.currentLogStream = this.logStreams[index];
            this.currentLogStreamId = this.currentLogStream.id;
            if (this.tabItems[streamIndex + 1].slot == 'config') {
                this.tabItems.splice(4, 0, {
                    name: this.currentLogStreamId,
                    id: this.currentLogStreamId,
                    slot: this.currentLogStreamId

                });

            } else {
                this.tabItems.splice(4, 1, {
                    name: this.currentLogStreamId,
                    id: this.currentLogStreamId,
                    slot: this.currentLogStreamId

                });

            }
            this.$store.dispatch('setActiveTab', this.currentLogStreamId);
            this.getLogStreamByID(this.currentLogStream);
        },
        onDetailPageOpenClose(value) {
            this.showCloseBtn = false;
            this.detailPageOpen = value;
        },
        onDetailPageHover(value) {
            this.showCloseBtn = value;
        },
        openModal(data) {
            this.showModal({
                component: "ViewLog", data: {
                    sandboxName: this.$store.state.sandbox.currentSandbox, jobName: this.job.name, executionId: this.executionId,
                    screenlarge: true,
                    status: this.status.match(/\brunning\b/i) ? "running" : "",
                    streams: true,
                    streamData: data,
                    baseEndpoint: this.baseEndpoint
                }
            })
        },
        handleCustomRange({ startTime, endTime }) {
            this.customStartTime = startTime;
            this.customEndTime = endTime;
        },
        async onRefreshClick() {
            this.refreshBtnLoading = true
            this.$emit('hideReRunJobBtn', true);
            if (this.job?.kind?.toLowerCase() === 'pipeline' && !this.pipelineTracking) {
                this.trackPipelineExecutionStatus(this.executionId, true);
            }
            await this.getExecutionStatus();
            this.checkAndGetCountsMetrics();
            this.getExecutionLogs();
            this.getExecutors();
            this.getLogStreams();
            this.getExecutionConfig();
            this.retryProgressTrigger = !this.retryProgressTrigger
            this.refreshBtnLoading = false
        },
        async onShowMoreClick() {
            this.count = 1
            this.showMoreBtnLoading = true
            await this.handleJobChecking()
            this.showMoreBtnLoading = false
        },
        async readDestinationPromotionJob() {
            if (!this.isPromotion || !this.promotion) return;
            const params = {
                sandboxName: this.promotion.dst_sandbox_name,
                jobName: this.promotion.dst_job_name
            }
            await this.readJob(params).then(resp => {
                if (resp.data) {
                    this.promotedJobKind = resp.data.kind
                }
            }).catch(err => {
                console.error(err)
            })
        },
        async trackPipelineExecutionStatus(executionId, getCountsMetrics = false) {
            if (!executionId) {
                console.error('No running execution id found to track pipeline execution status...');
                return;
            }

            this.nodeMetricsInfo = {};
            this.pipelineTracking = true;
            this.pipelineExecutionDetails = {};

            // this loop is to wait till the execution starts running    
            let retryCount = 25;
            try {
                while (retryCount > 0) {
                    const response = await axios.get(
                        `${this.baseEndpoint}/jobs/${this.job?.name}/executions/${executionId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${this.$store.state.accessToken}`,
                            },
                        }
                    );
                    if (response.data?.status != 'SUBMITTED') {
                        break;
                    }
                    await new Promise(resolve => setTimeout(resolve, 5000)); // wait for 5 seconds
                    retryCount--;
                }
            } catch (error) {
                console.error('Error fetching pipeline job execution status: ', error);
            }

            this.pipelineRunning = true;
            try {
                // infinite loop until the component is unmounted or the pipeline is not running
                while (!this.pipelineBreakCheck) {
                    let error = null;
                    let response;
                    let details = {
                        executionDetails: {},
                        executionDetailsByName: {}, // Also maintain a name-keyed bucket (used by in_loop display nodes)
                    };
                    try {
                        response = await this.getPipelineExecutionDetails(this.job?.name, executionId);
                    } catch (e) {
                        error = e.response?.data?.error || e.message;
                        console.error('Error fetching pipeline execution details: ', error);
                    }
                    if (error) break;

                    // parse the response
                    response.data?.tasks?.forEach(task => {
                        const jobName = task.full_path.split('/').pop();
                        // Key by full_path for exact lookup (handles same job name in multiple positions)
                        details.executionDetails[task.full_path] = [task];

                        if (!details.executionDetailsByName[jobName]) details.executionDetailsByName[jobName] = [];
                        details.executionDetailsByName[jobName].push(task);
                    });
                    details = { ...details, ...response.data };
                    this.pipelineExecutionDetails = JSON.parse(JSON.stringify(details));
                    this.fetchExtraJobsInExecution(); // fetch extra jobs that is not in config. Eg, env variables in job configs, can only be fetched by this method.

                    if (getCountsMetrics) {
                        const pipelineJobExecutionDetails = JSON.parse(JSON.stringify(this.pipelineExecutionDetails));
                        // get the node metrics from counts service
                        const nodeMetricsPromises = Object.entries(pipelineJobExecutionDetails?.executionDetails || {}).map(([jobName, tasks]) => {
                            tasks.forEach((task, index) => {
                                // check if task has executions
                                if (!task.executions || !task.executions.length) return null;

                                // do not get metrics for tasks that are not running/completed
                                if (!task.status || ['waiting', 'submitted', 'not_started'].includes(task.status.toLowerCase())) return null;

                                // for each execution of the task, get the metrics
                                task.executions?.forEach(execution => {
                                    let executionId = execution?.execution_id || null;
                                    let jobName = task.full_path.split('/').pop() || null;

                                    // check if executionId and jobName are valid
                                    if (!executionId || !jobName) return null;

                                    // if running - get metrics
                                    // if completed - get metrics if not already fetched or fetch once if last status was running
                                    if (task.status.toLowerCase() === 'running' ||
                                        (['succeeded', 'failed', 'cancelled'].includes(task.status.toLowerCase()) && (!this.nodeMetricsInfo[`${jobName}-${executionId}`] || this.nodeMetricsInfo[`${jobName}-${executionId}`] == 'running'))) {

                                        console.log("getting metrics for job: ", jobName, "execution: ", executionId);
                                        // prepare counts payload
                                        let countsPayload = {
                                            filter_dimensions: {
                                                "cid": this.$store.state.currentClient.cid,
                                                "sandbox_name": this.sandboxId,
                                                "job_name": jobName,
                                                "job_run_id": executionId,
                                                "data_channel": "input"
                                            },
                                            metrics: {
                                                "amdp-metric": JSON.parse(JSON.stringify(this.$store.state.amdp.countsMetricReqObj?.["amdp-metric"] || {}))
                                            }
                                        };
                                        if (countsPayload?.metrics?.["amdp-metric"]) {
                                            countsPayload.metrics["amdp-metric"].header = ["data_channel_type", "record_count"]; // only record count is required as of now
                                        }
                                        return axios.post(`${this.$store.state.countsUrl}/v2/cids/${this.$store.state.currentClient.cid}/metrics/batch/query`, countsPayload, {
                                            headers: {
                                                Authorization: `Bearer ${this.$store.state.accessToken}`,
                                            },
                                        }).then(response => {
                                            if (response.data) {
                                                let resp = response.data;
                                                this.nodeMetricsInfo[`${jobName}-${executionId}`] = task.status.toLowerCase();
                                                if (resp.data?.["amdp-metric"]?.data?.length) {
                                                    let recordCount = resp.data?.["amdp-metric"]?.data?.[0]?.[1] || null; // first appearing record count is taken
                                                    if (!this.nodeMetricsInfo[jobName]) {
                                                        this.nodeMetricsInfo[jobName] = [];
                                                    }
                                                    this.nodeMetricsInfo[jobName].push(recordCount);
                                                } else console.log("No counts data found for job: ", jobName);
                                            }
                                        }).catch(error => {
                                            console.error('Error fetching counts metrics for job: ', jobName, 'execution: ', executionId, 'error: ', error);
                                            return null;
                                        });
                                    }
                                });
                            })
                        });
                        await Promise.allSettled(nodeMetricsPromises);
                    }

                    // now update the node info manually
                    if (this.nodeInfo?.length > 0 && this.currentNodeInfo) {
                        this.onNodeClick(
                            { name: this.currentNodeInfo.name, full_path: this.currentNodeInfo.full_path },
                            this.currentNodeInfo?.task_id || ""
                        );
                    }

                    // stop if pipeline is not running
                    if (response?.data?.status != 'RUNNING') break;
                    await new Promise(resolve => setTimeout(resolve, 5000)); // wait for 5 seconds
                }
            } catch (error) {
                console.error('Error fetching pipeline execution status: ', error);
            } finally {
                this.pipelineRunning = false;
                this.pipelineTracking = false;
            }
        },
        async getPipelineExecutionDetails(jobName, executionId) {
            return axios.get(
                `${this.baseEndpoint}/pipelines/${jobName}/executions/${executionId}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.accessToken}`,
                    },
                }
            ).catch((error) => {
                throw error;
            });
        },
        togglePipelineView(pipelineView) {
            this.pipelineView = pipelineView;
        },
        onNodeClick(nodePayload, taskId = "") {
            // nodePayload may be { name, full_path } (from pipelineGraph) or a plain string (legacy calls)
            const nodeName = typeof nodePayload === 'object' ? nodePayload.name : nodePayload;
            const fullPath = typeof nodePayload === 'object' ? nodePayload.full_path : null;

            let nodeInfo = null;

            if (fullPath && !this.isLoopNode(nodePayload)) {
                // Exact path lookup — one task per position, no collision
                const task = this.pipelineExecutionDetails?.executionDetails?.[fullPath]?.[0] || null;
                nodeInfo = task ? [task] : null;
            }

            // Fallback: name-bucket (YAML path, in_loop display nodes, or legacy string calls)
            if (!nodeInfo) {
                nodeInfo = this.pipelineExecutionDetails?.executionDetailsByName?.[nodeName] || this.pipelineExecutionDetails?.executionDetails?.[nodeName] || null;
            }

            if (!nodeInfo) {
                this.nodeInfo = null;
                this.currentNodeInfo = null;
                return;
            }

            // Attach name field to every item
            nodeInfo.forEach(item => { item.name = nodeName; });
            this.nodeInfo = JSON.parse(JSON.stringify(nodeInfo));
            this.currentNodeInfo = taskId
                ? this.nodeInfo.find(item => item.task_id === taskId) || null
                : this.nodeInfo[0];
            this.markSuccessError = null;
        },
        isLoopNode(nodeInfo) {
            // A node is a loop node only when its full_path contains /in_loop/
            return nodeInfo?.full_path?.includes('/in_loop/') || false;
        },
        async markTaskAsSuccess() {
            if (!this.nodeInfo || this.nodeInfo.length === 0 || !this.currentNodeInfo) return;
            this.markingTaskSuccess = true;
            this.markSuccessError = null;
            try {
                await axios.put(`${this.baseEndpoint}/pipelines/${this.job?.name}/executions/${this.executionId}/tasks`,
                    { tasks: [{ task_id: this.currentNodeInfo.task_id, mark_success: true }] }, {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.accessToken}`,
                    }
                })
                    .catch((error) => {
                        throw error;
                    });

                if (!this.pipelineTracking) await this.trackPipelineExecutionStatus(this.executionId);
            } catch (error) {
                console.error('Error marking task as successful: ', error);
                this.markSuccessError = error.response?.data?.error || error.message;
            } finally {
                this.markingTaskSuccess = false;
            }
        },
        async redirectToJob(currentNodeInfo) {
            if (!currentNodeInfo) return;
            const jobName = currentNodeInfo.name;
            const sandboxName = currentNodeInfo.sandbox_name || this.sandboxId;
            this.$router.push(this.isDevSandbox ? `/${this.$store.state.currentClient.cid}/amdp/sandbox/${sandboxName}/jobs/${jobName}/configuration` :
                `/${this.$store.state.currentClient.cid}/amdp/${sandboxName}/jobs/${jobName}/run-history`
            )
        },
        async fetchExtraJobsInExecution() {
            const pipelineJob = this.pipelineJobConfig || null;
            if (pipelineJob?.kind?.toLowerCase() === "pipeline") {
                if (!this.job.system_files) return null;
                const configFile = pipelineJob.system_files.find(
                    f => f.relative_path === 'config.yaml'
                );
                const varsFile = pipelineJob.system_files.find(
                    f => f.relative_path === 'vars.yaml'
                );
                if (!configFile?.content) return null;

                let jobsInfo = JSON.parse(JSON.stringify({ ...this.pipelineJobs, ...this.pipelineJobsAll }));
                try {
                    // merging is required, since the config file may refer anchors defined in vars file.
                    // yaml parser does not support a case where anchors are used but not defined.
                    const mergedFile = (varsFile?.content || '') + '\n' + (configFile?.content || '');

                    // parseAllDocuments returns an iterable of Document objects
                    const documents = YAML.parseAllDocuments(mergedFile);
                    if (!documents || documents.length === 0) return null;

                    const parsed = documents[documents.length - 1].toJS(); // get the last document parsed since config.yaml file is at last

                    const jobNamesFromYaml = this.getJobNames(parsed);
                    const jobNamesFromTasks = Object.keys(this.pipelineExecutionDetails?.executionDetailsByName || []);
                    const jobNames = [...new Set([...jobNamesFromYaml, ...jobNamesFromTasks])];

                    const readPromises = jobNames.map(async (jobName) => {
                        // if job not in jobsInfo, then fetch job details
                        if (!Object.keys(jobsInfo).includes(jobName)) {
                            return this.readSandboxJob(jobName).then(response => {
                                jobsInfo[jobName] = response.data;
                            });
                        }
                    });
                    const results = await Promise.allSettled(readPromises);
                } catch (e) {
                    console.error('YAML parse error', e);
                } finally {
                    this.pipelineJobsAll = JSON.parse(JSON.stringify(jobsInfo));
                }
            }
        },
        getJobNames(input, names = []) {
            if (input.in_sequence) {
                input.in_sequence.forEach(item => this.getJobNames(item, names));
            } else if (input.in_parallel) {
                input.in_parallel.forEach(item => this.getJobNames(item, names));
            } else if (input.job_name) {
                names.push(input.job_name);
            }
            return names;
        },
    },

    async beforeMount() {
        let self = this;
        this.getExecutionConfig();
        if (this.job?.kind?.toLowerCase() === 'pipeline') {
            this.trackPipelineExecutionStatus(this.executionId, true);
        }
        await this.handleJobChecking();
        this.checkAndGetCountsMetrics();
        this.getLogStreams();
        this.readDestinationPromotionJob();
        this.enableRefreshBtn = true;
        this.enableShowMoreBtn = true;

        this.logStreamColumns = [{ data: "id" },
        { data: "logs" },
        { data: "source", title: "Source" },
        { data: "type", title: "Type" },
        { data: "timestamp", title: "Timestamp" },
        {
            data: "size_in_bytes", title: "Size", render: function (data, type, row) {
                if (!data) return "";
                if (!self.showLogDataInUnits) return data;
                return formatBytes(data)
            }
        }
        ]
        await this.fetchExtraJobsInExecution();
    },

    beforeUnmount() {
        this.breakCheck = true;
        this.pipelineBreakCheck = true;
        this.$emit('setSaveConfigBtn', false);
        this.$emit('hideReRunJobBtn', true);
    },
    mounted() {
        this.$store.dispatch('setActiveTab', "Progress")
        this.$emit('hideEdit', true);
        this.$emit('hideRunJob', true);
        this.$emit('hidePromoteBtn', true);
        this.$emit('setSaveConfigBtn', true);
        this.$emit('hideReRunJobBtn', true);
    },
    emits: ['setRunClick', 'hideEdit', 'hideRunJob', 'showCancelJob', 'hidePromoteBtn', 'setSaveConfigBtn', 'getPromotionRunList', 'hideReRunJobBtn', 'reRunning']
};
</script>



<style scoped>
#logs {
    max-height: 60vh;
    border: 1px;
}

.no-border {
    box-shadow: none;
    border: none !important
}

.downloadLink {
    text-align: right;
}

.custom-refresh-btn {
    font-size: 15px;
    display: inline;
    /* Adjust text size */
}

.small-btn {
    font-size: 0.75rem;
    /* Reduce text size */
    line-height: 1;
    /* Make it compact */
    padding: 4px 6px;
    /* Very small padding */
}

/* Base style for the wrapper to ensure correct positioning context */
.node-info-wrapper {
    margin-top: 1rem;
    will-change: transform, opacity;
    /* Optimization for the animation engine */
}

/* Defining the Entry and Exit Active States (Transition duration and timing) */
.card-pop-enter-active {
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    /* A nice "back out" ease for a snappy pop */
}

.card-pop-leave-active {
    transition: all 0.2s ease-in;
    /* Faster fade out */
}

/* Defining the Start State for Entry / End State for Exit */
.card-pop-enter-from,
.card-pop-leave-to {
    opacity: 0;
    /* Start slightly smaller and lower down */
    transform: scale(0.9) translateY(10px);
}

.job-link-btn:hover {
    text-decoration: underline;
}

.task-item {
    background-color: #f9fbff;
    cursor: pointer;
    padding-left: 14px;
}

.task-item:first-child {
    border-top-right-radius: 0.25rem;
}

.task-item:last-child {
    border-bottom-right-radius: 0.25rem;
}

.active-task-item {
    background-color: #e8efff;
    font-weight: bold;
}
</style>

<style lang="scss">
@use '../../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .active-task-item {
        background-color: #002a65 !important;
        font-weight: bold !important;
    }
}
</style>